import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Select, Spin, Row } from 'antd'
import debounce from 'lodash/debounce'
import { DELAY } from 'const/delay'
import { searchRules } from 'domains/arcAds/api'
import Title from 'components/Title/Title'
import { StyledSelect, Id } from './RuleSearchSelect.styles'

const RuleSearchSelect = ({
  input: { value, onChange },
  title,
  meta,
  ...rest
}) => {
  // Handle both edit mode (raw value) and create mode (null/undefined)
  const displayValue = value || null

  const [rules, setRules] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [searchText, setSearchText] = useState('')

  const fetchRules = useCallback(async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setRules([])
      return
    }

    setIsFetching(true)
    try {
      const response = await searchRules(searchValue)
      if (response?.data?.data) {
        setRules(response.data.data)
      } else {
        setRules([])
      }
    } catch (error) {
      console.error('Error fetching rules:', error)
      setRules([])
    } finally {
      setIsFetching(false)
    }
  }, [])

  const onSearch = useMemo(
    () =>
      debounce(
        (value) => {
          setSearchText(value)
          fetchRules(value)
        },
        DELAY.onSearch
      ),
    [fetchRules]
  )

  const handleChange = (value) => {
    onChange(value)
  }

  const handleFocus = () => {
    setSearchText('')
    setRules([])
  }

  return (
    <Row>
      {title && <Title>{title}</Title>}
      <StyledSelect
        value={displayValue}
        onFocus={handleFocus}
        placeholder="Type at least 2 characters to search"
        filterOption={false}
        notFoundContent={isFetching ? <Spin size="small" /> : searchText.length < 2 ? 'Type at least 2 characters' : 'No results found'}
        showSearch
        onChange={handleChange}
        onSearch={onSearch}
        invalid={meta.modified && meta.error}
        {...rest}
      >
        {rules.map((rule) => {
          const id = rule.id
          const domain = rule.domain
          return (
            <Select.Option key={id} value={id}>
              <Row>
                <Id>{id}</Id> - {domain}
              </Row>
            </Select.Option>
          )
        })}
      </StyledSelect>
    </Row>
  )
}

RuleSearchSelect.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  title: PropTypes.string,
  meta: PropTypes.shape({
    error: PropTypes.string,
    modified: PropTypes.bool,
  }).isRequired,
}

RuleSearchSelect.defaultProps = {
  title: null,
}

export default RuleSearchSelect
